// src/components/aiinsight/ChatInputBar.jsx
// Antigravity-style input bar — input state is LOCAL to this component.
// Parent does NOT re-render on every keystroke.
// onSend(text) passes the typed text up only when submitted.

import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import {
    Box, CircularProgress, Checkbox, FormControlLabel, IconButton, InputBase, MenuItem,
    Popover, Typography, Chip, Skeleton, Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CableIcon from '@mui/icons-material/Cable';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckIcon from '@mui/icons-material/Check';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';

const FORMAT_OPTIONS = [
    { key: 'bullets',       label: 'Bullet Points',   desc: 'Present insights as structured bullets' },
    { key: 'summary',       label: 'Summary',          desc: 'Add a brief paragraph summary' },
    { key: 'compact',       label: 'Compact Numbers',  desc: 'Format numbers as $1.2M, 3.4K etc' },
    { key: 'dataMetrics',   label: 'Data Metrics',     desc: 'AI will pull relevant key metrics' },
    { key: 'recommendations', label: 'Similar follow up', desc: 'Suggest similar follow-up questions' },
];

const ChatInputBar = forwardRef(function ChatInputBar({
    // Parent no longer passes value/onChange — state is internal.
    // onSend(text) receives the current text when user submits.
    onSend,
    isLoading,
    disabled,
    placeholder,
    dataModels = [],
    selectedModelId = '',
    onModelChange,
    suggestions = [],
    suggestionsLoading = false,
    onSuggestionClick,
    onRefreshSuggestions,
    formatOptions = { bullets: true, summary: true, compact: true, dataMetrics: true, recommendations: false },
    onFormatChange,
}, ref) {
    // ── LOCAL input state — no parent re-render per keystroke ──────────────
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [formatAnchorEl, setFormatAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const formatOpen = Boolean(formatAnchorEl);

    const [dismissedSuggestions, setDismissedSuggestions] = useState(false);

    // Expose setValue imperatively so parent can fill (suggestion click) or clear the input
    useImperativeHandle(ref, () => ({
        setValue: (text) => {
            setValue(text);
            // Focus so user can edit if needed
            setTimeout(() => inputRef.current?.focus(), 0);
        },
        getValue: () => value,
        clear: () => setValue(''),
    }));

    const submit = () => {
        const text = value.trim();
        if (!text || isLoading || !selectedModelId) return;
        setValue('');
        onSend(text);    // pass text up — parent doesn't need to track it
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    const activeModel = dataModels.find((c) => c.id === selectedModelId);

    const handlePillClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleSelect = (id) => {
        onModelChange && onModelChange(id);
        setAnchorEl(null);
    };

    // Reset dismissal state whenever suggestions prop changes (new ones arrive)
    useEffect(() => {
        setDismissedSuggestions(false);
    }, [suggestions]);

    const handleSuggestionClickInternal = (text) => {
        if (isLoading) return;
        setDismissedSuggestions(true);
        onSuggestionClick?.(text);
    };

    return (
        <Box
            sx={{
                px: 2,
                pb: 1.5,
                pt: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
            }}
        >
            {/* ── AI Suggestions Horizontal List ── */}
            {!dismissedSuggestions && (suggestions.length > 0 || suggestionsLoading) && (
                <Box sx={{ 
                    position: 'relative', 
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover .hover-show': { opacity: 1 }
                }}>
                    {/* Left Scroll Arrow */}
                    <IconButton
                        className="hover-show"
                        onClick={() => {
                            if (scrollRef.current) scrollRef.current.scrollBy({ left: -250, behavior: 'smooth' });
                        }}
                        size="small"
                        sx={{
                            position: 'absolute', left: -8, zIndex: 3,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.8)' : 'rgba(241,245,249,0.85)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            opacity: 0, transition: 'opacity 0.2s',
                            '&:hover': { 
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.95)' : 'rgba(226,232,240,0.95)' 
                            }
                        }}
                    >
                        <KeyboardArrowLeftIcon fontSize="small" />
                    </IconButton>

                    {/* Scroll Container */}
                    <Box 
                        ref={scrollRef}
                        sx={{ 
                            display: 'flex', alignItems: 'center', gap: 1, 
                            overflowX: 'auto', py: 0.5,
                            width: '100%',
                            px: 1,
                            scrollBehavior: 'smooth',
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}
                    >
                        {suggestionsLoading ? (
                            [1, 2, 3, 4].map((i) => (
                                <Skeleton 
                                    key={i} 
                                    variant="rounded" 
                                    width={i === 2 ? 150 : i === 3 ? 130 : 110} 
                                    height={28} 
                                    animation="wave"
                                    sx={{ 
                                        borderRadius: '14px', 
                                        flexShrink: 0, 
                                        bgcolor: 'rgba(99,102,241,0.18)',
                                        '&::after': {
                                            background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.12), transparent)',
                                        }
                                    }} 
                                />
                            ))
                        ) : (
                            <>
                                {suggestions.map((sh, idx) => (
                                    <Chip
                                        key={idx}
                                        label={sh}
                                        onClick={() => handleSuggestionClickInternal(sh)}
                                        variant="outlined"
                                        icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important', color: '#6366f1 !important' }} />}
                                        sx={{
                                            height: 28,
                                            borderRadius: '14px',
                                            fontSize: '11.5px',
                                            borderColor: 'rgba(99,102,241,0.2)',
                                            bgcolor: 'background.paper',
                                            color: 'text.secondary',
                                            flexShrink: 0,
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: '#6366f1',
                                                bgcolor: 'rgba(99,102,241,0.04)',
                                                color: '#6366f1',
                                                transform: 'translateY(-1px)',
                                            },
                                            '& .MuiChip-icon': { ml: 1 }
                                        }}
                                    />
                                ))}
                            </>
                        )}
                    </Box>

                    {/* Right Scroll Arrow */}
                    <IconButton
                        className="hover-show"
                        onClick={() => {
                            if (scrollRef.current) scrollRef.current.scrollBy({ left: 250, behavior: 'smooth' });
                        }}
                        size="small"
                        sx={{
                            position: 'absolute', right: -8, zIndex: 3,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.8)' : 'rgba(241,245,249,0.85)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            opacity: 0, transition: 'opacity 0.2s',
                            '&:hover': { 
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.95)' : 'rgba(226,232,240,0.95)' 
                            }
                        }}
                    >
                        <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>

                    {/* Close Button - top right small, blurred, fill on hover */}
                    <IconButton
                        className="hover-show"
                        size="small"
                        onClick={() => setDismissedSuggestions(true)}
                        sx={{
                            position: 'absolute',
                            top: -12, 
                            right: -16, 
                            zIndex: 4,
                            width: 20, height: 20,
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.7)' : 'rgba(241,245,249,0.85)',
                            backdropFilter: 'blur(4px)',
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            opacity: 0, 
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: 'error.main',
                                color: 'white',
                                borderColor: 'error.main',
                            }
                        }}
                        title="Dismiss suggestions"
                    >
                        <ClearIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Box>
            )}

            {/* Input card — mirrors Antigravity's white rounded card */}
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: isLoading ? 'primary.light' : 'divider',
                    borderRadius: '12px',
                    bgcolor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.9)' : '#fff',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:focus-within': {
                        borderColor: 'rgba(99,102,241,0.6)',
                        boxShadow: '0 0 0 3px rgba(99,102,241,0.08)',
                    },
                    overflow: 'hidden',
                }}
            >
                {/* Textarea row */}
                <Box sx={{ px: 2, pt: 1.5 }}>
                    <InputBase
                        inputRef={inputRef}
                        multiline
                        maxRows={6}
                        fullWidth
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={
                            !selectedModelId
                                ? 'Select a data model to start chatting...'
                                : (placeholder || 'Ask anything about your data...')
                        }
                        disabled={isLoading || (!activeModel && !disabled)}
                        sx={{
                            width: '100%',
                            fontSize: 14,
                            color: 'text.primary',
                            lineHeight: 1.65,
                            '& textarea': { resize: 'none' },
                            '& textarea::placeholder': { color: 'text.disabled', opacity: 1 },
                        }}
                    />
                </Box>

                {/* Bottom row (same row as Antigravity's pills + send button) */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                    }}
                >
                    {/* LEFT — data model pill */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {dataModels.length > 0 && (
                            <>
                                <Box
                                    onClick={handlePillClick}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1.25,
                                        py: 0.4,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: '20px',
                                        cursor: 'pointer',
                                        bgcolor: 'transparent',
                                        transition: 'all 0.15s',
                                        userSelect: 'none',
                                        '&:hover': {
                                            borderColor: 'rgba(99,102,241,0.5)',
                                            bgcolor: 'rgba(99,102,241,0.05)',
                                        },
                                    }}
                                >
                                    <CableIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {activeModel ? activeModel.name : 'Select data model'}
                                    </Typography>
                                    <KeyboardArrowDownIcon
                                        sx={{ fontSize: 14, color: 'text.disabled' }}
                                    />
                                </Box>

                                {/* Popover picker */}
                                <Popover
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={() => setAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    PaperProps={{
                                        sx: {
                                            mt: -0.5,
                                            minWidth: 200,
                                            maxHeight: 260,
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                            py: 0.5,
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            px: 2,
                                            py: 0.75,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: 'text.disabled',
                                            letterSpacing: 0.8,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Data Model
                                    </Typography>
                                    {dataModels.map((model) => (
                                        <MenuItem
                                            key={model.id}
                                            onClick={() => handleSelect(model.id)}
                                            sx={{
                                                px: 2,
                                                py: 0.75,
                                                fontSize: 13,
                                                gap: 1,
                                                color: model.id === selectedModelId ? 'primary.main' : 'text.primary',
                                                fontWeight: model.id === selectedModelId ? 600 : 400,
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <CableIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                                            <Box sx={{ flex: 1 }}>{model.name}</Box>
                                            {model.id === selectedModelId && (
                                                <CheckIcon sx={{ fontSize: 14 }} />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Popover>
                            </>
                        )}
                    </Box>

                    {/* RIGHT — format & send buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Insight Format Settings">
                            <IconButton
                                onClick={(e) => setFormatAnchorEl(e.currentTarget)}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: formatOpen ? 'primary.main' : 'divider',
                                    bgcolor: formatOpen ? 'rgba(99,102,241,0.05)' : 'transparent',
                                    color: formatOpen ? 'primary.main' : 'text.secondary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.light',
                                        bgcolor: 'rgba(99,102,241,0.08)',
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                <TuneIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>

                        <IconButton
                            onClick={submit}
                            disabled={isLoading || !activeModel || !value.trim()}
                            size="small"
                            sx={{
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                                bgcolor:
                                    isLoading || !activeModel || !value.trim()
                                        ? (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.08)'
                                                : 'rgba(0,0,0,0.06)'
                                        : 'text.primary',
                                color:
                                    isLoading || !activeModel || !value.trim()
                                        ? 'text.disabled'
                                        : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover:not(:disabled)': {
                                    bgcolor: 'text.primary',
                                    transform: 'scale(1.06)',
                                },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={14} sx={{ color: 'text.disabled' }} />
                            ) : (
                                <SendIcon sx={{ fontSize: 14 }} />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* Format popover */}
            <Popover
                open={formatOpen}
                anchorEl={formatAnchorEl}
                onClose={() => setFormatAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: -1, minWidth: 260,
                        border: '1px solid', borderColor: 'divider',
                        borderRadius: 2.5,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                        py: 1, px: 0.5,
                    },
                }}
            >
                <Typography sx={{ px: 2, py: 0.75, fontSize: 10, fontWeight: 700, color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                    Insight Format Options
                </Typography>
                {FORMAT_OPTIONS.map(opt => (
                    <Box key={opt.key}
                        onClick={() => onFormatChange?.({ ...formatOptions, [opt.key]: !formatOptions[opt.key] })}
                        sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                            px: 1.5, py: 0.75, cursor: 'pointer', borderRadius: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'background 0.12s',
                        }}
                    >
                        <Checkbox
                            checked={!!formatOptions[opt.key]}
                            size="small"
                            sx={{
                                p: 0, color: 'divider', mt: '1px',
                                '&.Mui-checked': { color: '#6366f1' },
                                flexShrink: 0,
                            }}
                            onClick={e => e.stopPropagation()}
                            onChange={() => onFormatChange?.({ ...formatOptions, [opt.key]: !formatOptions[opt.key] })}
                        />
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: formatOptions[opt.key] ? 600 : 400, color: 'text.primary', lineHeight: 1.2 }}>
                                {opt.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10.5 }}>
                                {opt.desc}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Popover>
        </Box>
    );
});

export default ChatInputBar;
